import {
  ContentLayout,
  HeaderBreadcrumbs,
  Loader,
  PageNotFound,
  useAppConfig,
  useDropdownApis,
  CountrySelect,
} from "../../../shared";
import * as React from "react";
import {
  Button,
  Stack,
  Card,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import InputControl from "../../../shared/components/InputControl";
import { PHONE_NUMBER_VALIDATION, getPhoneNumberRule } from "../../../shared/constants/validation";
import { useLocation, useParams } from "react-router-dom";
import { useCustomers } from "../hooks";
import SimpleReactValidator from "simple-react-validator";

export const CreateOrEditCustomers = () => {
  const { id } = useParams();
  const { taxIdTerm } = useAppConfig();
  const { statesList } = useDropdownApis({
    isStatesList: true,
  });
  const updateMode = id ? true : false;
  const [, forceUpdate] = React.useState();
  const validator = React.useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
    })
  );
  const {
    onChange,
    state,
    onSaveCustomer,
    onUpdateCustomer,
    globalData,
    onDateChange,
    validateDate,
    setState,
    isStoreAdmin,
  } = useCustomers({
    load: false,
    id: id,
  });
  const { pathname } = useLocation();
  const isClient = pathname.includes("client-account");
  const {
    isSaveButtonBusy,
    customerName,
    httpStatusCodes,
    validation,
    isBussiness,
    isDetailsFetching,
  } = state;
  const {
    first_name,
    last_name,
    email,
    country_code,
    phone,
    date_of_birth,
    gender,
    status,
    is_business_user,
    opt_in,
    business_name,
    business_branch,
    business_country_code,
    business_phone,
    abn_number,
    full_business_address,
    business_state,
    business_postcode,
    have_a_card,
    member_number,
    verification_code,
  } = state.data;
  const shouldValidateBusinessFields = Boolean(is_business_user);

  const onSubmit = () => {
    if (validator.current.allValid() && !validation.date_of_birth) {
      updateMode ? onUpdateCustomer(id) : onSaveCustomer();
    } else {
      validator.current.showMessages();
      forceUpdate(1);
      setState((draft) => {
        draft.validation.isValidated = true;
      });
    }
  };
  if (httpStatusCodes.details === 404) {
    return <PageNotFound />;
  }

  return (
    <>
      <ContentLayout
        title={updateMode ? customerName : "New Customer"}
        rightContent={
          <>
            <Button
              onClick={() => onSubmit()}
              type="button"
              size="medium"
              variant="contained"
              disabled={isSaveButtonBusy}
            >
              {id ? "Update" : "Save"}
            </Button>
          </>
        }
        breadcrumbs={
          !id ? (
            <>
              <HeaderBreadcrumbs
                links={
                  !isClient
                    ? [
                        {
                          name: "Dashboard",
                          href: isStoreAdmin ? "/store-admin" : "/",
                        },
                        {
                          name: "Customers",
                          href: isStoreAdmin
                            ? "store-admin/customers"
                            : "/customers",
                        },

                        { name: "create" },
                      ]
                    : [
                        {
                          name: "Dashboard",
                          href: isStoreAdmin ? "/store-admin" : "/",
                        },
                        {
                          name: "Client Account",
                          href: isStoreAdmin
                            ? "/store-admin/client-account"
                            : "/client-account",
                        },

                        {
                          name: "Client Account Create",
                          href: isStoreAdmin
                            ? "/store-admin/client-account/create"
                            : "/client-account/create",
                        },
                        { name: "Create" },
                      ]
                }
              />
            </>
          ) : (
            <>
              <HeaderBreadcrumbs
                links={[
                  {
                    name: "Dashboard",
                    href: isStoreAdmin ? "/store-admin" : "/",
                  },
                  {
                    name: "Customers",
                    href: isStoreAdmin
                      ? "/store-admin/customers"
                      : "/customers",
                  },
                  {
                    name: customerName,
                    href: isStoreAdmin
                      ? `/store-admin/customers/details/${id}`
                      : `/customers/details/${id}`,
                  },
                  { name: "Edit" },
                ]}
              />
            </>
          )
        }
      >
        {id && isDetailsFetching ? (
          <Loader />
        ) : (
          <>
            {validator.current.purgeFields()}
            <Card sx={{ p: "32px 24px", mb: "24px" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ width: "150px" }}
                  >
                    Basic Details
                  </Typography>
                </Grid>
                <Grid item sm={8}>
                  <Stack width={"100%"} gap="20px">
                    <Stack direction="row" gap="20px">
                      <InputControl
                        name="first_name"
                        label="First Name"
                        value={first_name}
                        onChange={onChange}
                        required
                        error={validator.current.message(
                          "First Name",
                          first_name,
                          "required",
                        )}
                        helperText={validator.current.message(
                          "First Name",
                          first_name,
                          "required",
                        )}
                      />
                      <InputControl
                        name="last_name"
                        label="Last Name"
                        required
                        value={last_name}
                        onChange={onChange}
                        error={validator.current.message(
                          "Last Name",
                          last_name,
                          "required",
                        )}
                        helperText={validator.current.message(
                          "Last Name",
                          last_name,
                          "required",
                        )}
                      />
                    </Stack>
                    <InputControl
                      name="email"
                      label="Email Address"
                      value={email}
                      onChange={onChange}
                      required
                      error={validator.current.message(
                        "Email",
                        email,
                        "required|email",
                      )}
                      helperText={validator.current.message(
                        "Email",
                        email,
                        "required|email",
                      )}
                    />
                    <Stack direction="row" gap="20px">
                      <CountrySelect
                        name="country_code"
                        value={country_code}
                        onChange={(newValue) =>
                          onChange({
                            target: {
                              name: "country_code",
                              value: newValue?.value,
                            },
                          })
                        }
                        error={validator.current.message(
                          "Country Code",
                          country_code,
                          "required",
                        )}
                        helperText={validator.current.message(
                          "Country Code",
                          country_code,
                          "required",
                        )}
                        required
                      />

                      <InputControl
                        name="phone"
                        label="Phone Number"
                        type="text"
                        value={phone}
                        onChange={onChange}
                        required
                        error={validator.current.message(
                          "Phone Number",
                          phone,
                          `required|${getPhoneNumberRule(country_code)}`,
                          PHONE_NUMBER_VALIDATION,
                        )}
                        helperText={validator.current.message(
                          "Phone Number",
                          phone,
                          `required|${getPhoneNumberRule(country_code)}`,
                          PHONE_NUMBER_VALIDATION,
                        )}
                      />
                    </Stack>

                    <InputControl
                      type="datePicker"
                      name="date_of_birth"
                      label="Date Of Birth"
                      inputFormat="dd/MM/yyyy"
                      disableFuture
                      value={date_of_birth}
                      onChange={(value, keyboardInputValue) =>
                        onDateChange(value, keyboardInputValue)
                      }
                      onError={(reason, value) =>
                        validateDate(reason, "date_of_birth")
                      }
                      error={
                        state.validation.isValidated &&
                        state.validation.date_of_birth
                      }
                      helperText={
                        state.validation.isValidated &&
                        state.validation.date_of_birth
                      }
                    />
                    <InputControl
                      type="dropdown"
                      disableClearable
                      id="gender"
                      getOptionLabel={(option) => option.label}
                      value={
                        globalData?.gender?.length > 0 && gender
                          ? globalData.gender.find((c) => c.value === gender)
                          : null
                      }
                      options={globalData.gender ?? []}
                      onChange={(e, newValue) => {
                        onChange({
                          target: { name: "gender", value: newValue.value },
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Gender"
                          required
                          error={validator.current.message(
                            "Gender",
                            gender,
                            "required",
                          )}
                          helperText={validator.current.message(
                            "Gender",
                            gender,
                            "required",
                          )}
                        />
                      )}
                    />
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 1, sm: 11 }}
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Stack spacing={1}>
                        <h4>Status</h4>
                      </Stack>
                      <InputControl
                        type="switch"
                        value={status}
                        onChange={(e) =>
                          onChange({
                            target: {
                              name: "status",
                              value: e.target.checked ? 1 : 0,
                            },
                          })
                        }
                      />
                    </Stack>
                    <FormControlLabel
                      sx={{ mx: 0 }}
                      control={
                        <Checkbox
                          edge="start"
                          tabIndex={-1}
                          disableRipple
                          checked={opt_in}
                          onChange={(e) =>
                            onChange({
                              target: {
                                name: "opt_in",
                                value: e.target.checked ? 1 : 0,
                              },
                            })
                          }
                        />
                      }
                      label="Opt-In"
                    />
                  </Stack>
                </Grid>
              </Grid>

              {!id && (
                <>
                  {" "}
                  <Divider sx={{ m: "16px -24px" }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        sx={{ width: "150px" }}
                      >
                        Do you have a card?
                      </Typography>
                    </Grid>
                    <Grid item sm={8}>
                      <Stack gap="20px">
                        <InputControl
                          type="switch"
                          value={have_a_card}
                          onChange={(e) =>
                            onChange({
                              target: {
                                name: "have_a_card",
                                value: e.target.checked ? 1 : 0,
                              },
                            })
                          }
                        />
                        {!!have_a_card && (
                          <Stack direction="row" gap="20px">
                            <InputControl
                              name="member_number"
                              label="Member Number"
                              value={state?.data?.member_number}
                              onChange={onChange}
                              error={validator.current.message(
                                "Member Number",
                                member_number,
                                "required",
                              )}
                              helperText={validator.current.message(
                                "Member Number",
                                member_number,
                                "required",
                              )}
                              required
                            />
                            <InputControl
                              name="verification_code"
                              label="Verification Code"
                              value={state.data.verification_code}
                              onChange={onChange}
                              error={validator.current.message(
                                "Verification Code",
                                verification_code,
                                "required",
                              )}
                              helperText={validator.current.message(
                                "Verification Code",
                                verification_code,
                                "required",
                              )}
                              required
                            />
                          </Stack>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                </>
              )}
              <Divider sx={{ m: "16px -24px" }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ width: "150px" }}
                  >
                    Business User
                  </Typography>
                </Grid>
                <Grid item sm={8}>
                  <Stack gap="20px">
                    <InputControl
                      type="switch"
                      value={is_business_user}
                      onChange={(e) =>
                        onChange({
                          target: {
                            name: "is_business_user",
                            value: e.target.checked ? 1 : 0,
                          },
                        })
                      }
                    />
                    {!!isBussiness && (
                      <>
                        <InputControl
                          name="business_name"
                          label="Business Name"
                          value={business_name}
                          onChange={onChange}
                          required={shouldValidateBusinessFields}
                          error={shouldValidateBusinessFields && validator.current.message(
                            "Business Name",
                            business_name,
                            "required",
                          )}
                          helperText={shouldValidateBusinessFields && validator.current.message(
                            "Business Name",
                            business_name,
                            "required",
                          )}
                          disabled={!is_business_user && id}
                        />
                        <Stack direction="row" gap="20px">
                          <CountrySelect
                            name="business_country_code"
                            value={business_country_code}
                            onChange={(newValue) =>
                              onChange({
                                target: {
                                  name: "business_country_code",
                                  value: newValue?.value,
                                },
                              })
                            }
                            disabled={!is_business_user && !!id}
                          />
                          <InputControl
                            name="business_phone"
                            label="Business Phone Number"
                            type="text"
                            value={business_phone}
                            onChange={onChange}
                            error={shouldValidateBusinessFields && validator.current.message(
                              "Business Phone Number",
                              business_phone,
                              getPhoneNumberRule(business_country_code),
                              PHONE_NUMBER_VALIDATION,
                            )}
                            helperText={shouldValidateBusinessFields && validator.current.message(
                              "Business Phone Number",
                              business_phone,
                              getPhoneNumberRule(business_country_code),
                              PHONE_NUMBER_VALIDATION,
                            )}
                            disabled={!is_business_user && id}
                          />
                        </Stack>
                        <InputControl
                          type="textarea"
                          rows={3}
                          name="full_business_address"
                          label="Address"
                          value={full_business_address}
                          onChange={onChange}
                          required={shouldValidateBusinessFields}
                          error={shouldValidateBusinessFields && validator.current.message(
                            "Address",
                            full_business_address,
                            "required",
                          )}
                          helperText={shouldValidateBusinessFields && validator.current.message(
                            "Address",
                            full_business_address,
                            "required",
                          )}
                          disabled={!is_business_user && id}
                        />
                        <Stack direction="row" gap="20px">
                          <InputControl
                            type="dropdown"
                            disableClearable
                            getOptionLabel={(option) => option.name}
                            disabled={!is_business_user && id}
                            value={
                              statesList.find((c) => c.name === business_state) ??
                              null
                            }
                            options={statesList ?? []}
                            onChange={(e, v) => {
                              onChange({
                                target: {
                                  name: "business_state",
                                  value: v?.name ?? "",
                                },
                              });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="State"
                                required={shouldValidateBusinessFields}
                                error={shouldValidateBusinessFields && validator.current.message(
                                  "State",
                                  business_state,
                                  "required",
                                )}
                                helperText={shouldValidateBusinessFields && validator.current.message(
                                  "State",
                                  business_state,
                                  "required",
                                )}
                              />
                            )}
                          />
                          <InputControl
                            name="business_postcode"
                            label="Post Code"
                            value={business_postcode}
                            onChange={onChange}
                            required={shouldValidateBusinessFields}
                            error={shouldValidateBusinessFields && validator.current.message(
                              "Post Code",
                              business_postcode,
                              "required",
                            )}
                            helperText={shouldValidateBusinessFields && validator.current.message(
                              "Post Code",
                              business_postcode,
                              "required",
                            )}
                            disabled={!is_business_user && id}
                          />
                        </Stack>
                        <InputControl
                          name="abn_number"
                          label={taxIdTerm}
                          value={abn_number}
                          onChange={onChange}
                          required={shouldValidateBusinessFields}
                          error={shouldValidateBusinessFields && validator.current.message(
                            taxIdTerm,
                            abn_number,
                            "required",
                          )}
                          helperText={shouldValidateBusinessFields && validator.current.message(
                            taxIdTerm,
                            abn_number,
                            "required",
                          )}
                          disabled={!is_business_user && id}
                        />
                        <InputControl
                          name="business_branch"
                          label="Branch"
                          value={business_branch}
                          onChange={onChange}
                          disabled={!is_business_user && id}
                        />
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Card>
          </>
        )}
      </ContentLayout>
    </>
  );
};
